/**
 * Allys Theme Currency Switcher
 * 
 * Themed wrapper around the core CurrencySelector component.
 * Integrates with CurrencyContext for app-wide currency conversion.
 * 
 * Works correctly in both desktop (portal-based) and mobile menu (inline) contexts
 */

'use client';

import { CurrencySelector } from '@/components/ui/CurrencySelector';
import { useCurrencyOptional } from '@/contexts/CurrencyContext';
import type { Currency } from '@/lib/config/app.config';
import { AnimatePresence, motion } from 'framer-motion';
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CURRENCY_CONFIG, DEFAULT_CURRENCY, SUPPORTED_CURRENCIES } from '@/lib/config/app.config';

interface CurrencySwitcherProps {
  className?: string;
  /** 
   * If true, uses the core CurrencySelector component 
   * If false, uses the custom Allys-styled dropdown (default)
   */
  useCore?: boolean;
  /** 
   * When true, renders dropdown inline instead of via portal
   * Use in mobile menu context where portal positioning doesn't work correctly
   */
  inMobileMenu?: boolean;
}

/**
 * CurrencySwitcher - Allys-themed currency selector
 * 
 * Now properly integrates with CurrencyContext for app-wide currency conversion.
 * When user selects a currency, all FormatPrice components will update automatically.
 */
export function CurrencySwitcher({ 
  className = '',
  useCore = false,
  inMobileMenu = false,
}: CurrencySwitcherProps) {
  // Use the core CurrencySelector if requested
  if (useCore) {
    return (
      <CurrencySelector
        variant="minimal"
        className={className}
        showLoadingState={true}
      />
    );
  }

  // Otherwise render the custom Allys-styled version
  return <AllysCurrencyDropdown className={className} inMobileMenu={inMobileMenu} />;
}

/**
 * Custom Allys-styled currency dropdown
 * Integrates with CurrencyContext for proper currency switching
 */
function AllysCurrencyDropdown({ className = '', inMobileMenu = false }: { className?: string; inMobileMenu?: boolean }) {
  const currencyContext = useCurrencyOptional();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Use ref for button rect to avoid state timing issues
  const buttonRectRef = useRef<DOMRect | null>(null);
  const [, forceUpdate] = useState(0);
  const [mounted, setMounted] = useState(false);

  const selectedCurrency = currencyContext?.selectedCurrency ?? DEFAULT_CURRENCY;
  const isLoading = currencyContext?.isLoading ?? false;
  

  // Get currency display info
  const currentCurrencyConfig = CURRENCY_CONFIG[selectedCurrency as Currency];
  const currentCurrencyData = {
    code: selectedCurrency,
    symbol: currentCurrencyConfig?.symbol ?? selectedCurrency,
    name: currentCurrencyConfig?.name ?? selectedCurrency,
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current && !inMobileMenu) {
      buttonRectRef.current = buttonRef.current.getBoundingClientRect();
      forceUpdate(n => n + 1);
    }
  }, [isOpen, inMobileMenu]);

  // Handle click outside for inline dropdown
  useEffect(() => {
    if (!isOpen || !inMobileMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, inMobileMenu]);

  const handleCurrencySelect = useCallback((currency: Currency) => {
    if (currencyContext) {
      currencyContext.setSelectedCurrency(currency);
    }
    setIsOpen(false);
  }, [currencyContext]);

  // Dropdown content (shared between portal and inline rendering)
  const dropdownContent = (
    <div className="py-2 max-h-[280px] overflow-y-auto">
      {SUPPORTED_CURRENCIES.map((currency) => {
        const config = CURRENCY_CONFIG[currency];
        const info = {
          code: currency,
          symbol: config?.symbol ?? currency,
          name: config?.name ?? currency,
        };
        const isSelected = currency === selectedCurrency;

        return (
          <button
            key={currency}
            onClick={() => handleCurrencySelect(currency)}
            className={`w-full px-4 py-3 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors duration-200 flex items-center justify-between [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] min-h-[48px] touch-manipulation ${
              isSelected ? 'bg-gray-50' : ''
            }`}
            role="option"
            aria-selected={isSelected}
          >
            <div className="flex items-center gap-3">
              <span className="font-semibold text-gray-900 text-base w-6">{info.symbol}</span>
              <div className="flex flex-col">
                <span className="font-semibold text-gray-900 text-sm">{info.code}</span>
                <span className="text-xs text-gray-500">{info.name}</span>
              </div>
            </div>
            {isSelected && (
              <svg className="w-5 h-5 text-[#243462] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Currency Button */}
      <button
        ref={buttonRef}
        onClick={() => {
          // Set buttonRect ref immediately before toggling isOpen to ensure portal renders
          if (!isOpen && buttonRef.current && !inMobileMenu) {
            buttonRectRef.current = buttonRef.current.getBoundingClientRect();
          }
          setIsOpen(!isOpen);
        }}
        className={`inline-flex items-center gap-2 rounded-lg bg-transparent border-0 text-gray-700 hover:text-gray-900 hover:bg-gray-100/80 active:bg-gray-200/80 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300 font-medium text-sm [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] touch-manipulation ${
          inMobileMenu 
            ? 'px-4 py-3 min-h-[48px] min-w-[80px] justify-center' 
            : 'px-5 py-2'
        } ${isLoading ? 'opacity-75' : ''}`}
        aria-label={`${currentCurrencyData.code}: Select currency`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span>{currentCurrencyData.code}</span>
        {isLoading ? (
          <span className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
        ) : (
          <motion.svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2}
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </motion.svg>
        )}
      </button>

      {/* Mobile Dropdown (Inline) */}
      {inMobileMenu && (
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute left-1/2 -translate-x-1/2 top-full mt-2 min-w-[200px] bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-[100]"
              role="listbox"
            >
              {dropdownContent}
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* Desktop Dropdown (Portal) */}
      {!inMobileMenu && mounted && createPortal(
        <AnimatePresence>
          {isOpen && buttonRectRef.current && (
            <>
              {/* Backdrop */}
              <motion.div
                key="currency-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="fixed inset-0 z-[9999]"
                onClick={() => setIsOpen(false)}
                aria-hidden="true"
              />
              <motion.div
                key="currency-dropdown"
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'fixed',
                  top: buttonRectRef.current.bottom + 8,
                  right: window.innerWidth - buttonRectRef.current.right,
                }}
                className="min-w-[220px] bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-[10000]"
                role="listbox"
              >
                {dropdownContent}
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
